import React, { useState, useEffect } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { useParams } from 'react-router-dom';
import ClinicReminderSettingsForm from './ClinicReminderSettingsForm';
import { toast } from 'react-toastify';
import Loading from "../../../../common/Loading";

const ReminderTab = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { clinicId } = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [reminderSettings, setReminderSettings] = useState({});
    const [lastModified, setLastModified] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        clinicService.getReminderSettings(clinicId)
            .then(res => {
                setIsLoading(false);
                setIsError(false);
                setErrorMessage('');
                setReminderSettings(res.data);
                setLastModified(res.data.modifiedAt);
            })
            .catch(err => {
                setIsLoading(false);
                setIsError(true);
                setErrorMessage(err.response.data.message);
            });
    }, [clinicId]);

    const updateReminderSettings = (rsettings) => {
        setReminderSettings(rsettings);
        setIsEditing(false);
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(clinicId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getStatusBadge = (value) => {
        if (typeof value === 'boolean') {
            return value ? (
                <span className="badge bg-success bg-opacity-10 text-success">
                    <i className="bi bi-check-circle-fill me-1"></i> Enabled
                </span>
            ) : (
                <span className="badge bg-danger bg-opacity-10 text-danger">
                    <i className="bi bi-x-circle-fill me-1"></i> Disabled
                </span>
            );
        }
        return <strong>{value}</strong>;
    };

    const getPreferenceText = (value) => {
        switch(value) {
            case "CLINIC" : return "Clinic";
            case "ECP": return "Ecp";
            case "BOTH": return "Both";
            default: return "Not set";
        }
    };

    const getCallPreferenceText = (value) => {
        switch(value) {
            case "DISABLED": return "Disabled";
            case "HOME_PHONE" : return "Home Phone";
            case "CELL_PHONE": return "Cell Phone";
            default: return "Not set";
        }
    };

    return (
        <div className="col-lg-12">
            <div className="card-padding shadow-none">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="card-title mb-0" style={{color: "#012970"}}>
                        <i className="bi bi-bell me-2"></i> Clinic Reminder Settings
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="badge bg-light text-dark me-2">
                            Clinic ID: {clinicId}
                        </span>
                        <button
                            onClick={copyToClipboard}
                            className="btn btn-sm btn-outline-primary"
                            title="Copy to clipboard"
                        >
                            {copied ? (
                                <i className="bi bi-check2"></i>
                            ) : (
                                <i className="bi bi-copy"></i>
                            )}
                        </button>
                        {isEditing ? (
                            <button
                                className="btn btn-outline-danger btn-sm ms-2"
                                onClick={() => setIsEditing(false)}
                            >
                                <i className="bi bi-x-lg me-1"></i> Cancel
                            </button>
                        ) : (
                            <button
                                className="btn btn-outline-primary btn-sm ms-2"
                                onClick={() => setIsEditing(true)}
                            >
                                <i className="bi bi-pencil me-1"></i> Edit
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <Loading />
                ) : isEditing ? (
                    <ClinicReminderSettingsForm
                        reminderSettings={reminderSettings}
                        updateSettings={updateReminderSettings}
                    />
                ) : (
                    <>
                        {lastModified && (
                            <div className="d-flex justify-content-end mb-3">
                                <small className="text-muted">
                                    Last Modified: {lastModified}
                                </small>
                            </div>
                        )}
                        <div className="row">
                            <div className="col-md-7">
                                <div className="list-group mb-3">
                                    {[
                                        { key: 'apptReminder', label: 'Appointment Reminder' },
                                        { key: 'isPrebookedReminder', label: 'Prebooked Reminder' },
                                        { key: 'isFirstReminder', label: 'First Reminder' },
                                        { key: 'isSecondReminder', label: 'Second Reminder' },
                                        { key: 'isLastReminder', label: 'Last Reminder' },
                                        { key: 'isSameDayReminder', label: 'Same Day Reminder' },
                                        { key: 'isClReplacement', label: 'Contact Lens Replacement Reminder' },
                                        { key: 'isClReorder', label: 'Contact Lens Reorder' },
                                        { key: 'isBirthday', label: 'Birthday Reminder' },
                                        { key: 'isThankyou', label: 'Thank You Reminder' },
                                        { key: 'isWelcome', label: 'Welcome Reminder' },
                                        { key: 'isEmail', label: 'Email Reminder' },
                                        { key: 'isSms', label: 'SMS Reminder' },
                                        { key: 'isCall', label: 'Call Reminder' },
                                        { key: 'isGlPickup', label: 'Glasses Pickup' },
                                        { key: 'isClPickup', label: 'Contact Lens Pickup' },
                                        { key: 'isFollowup', label: 'Follow Up' },
                                        { key: 'isApptSummary', label: 'Appointment Summary' },
                                        { key: 'isClGlSummary', label: 'Contact Lens & Glass Summary' }
                                    ].map((item) => (
                                        <div key={item.key} className="list-group-item">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>{item.label}</span>
                                                {getStatusBadge(reminderSettings[item.key])}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="col-md-5">
                                <div className="card card-border card-padding shadow-none">
                                    <div className="card-title"> Reminder Preference </div>
                                    <div className="card-body">
                                        <ul className="list-group list-group-flush">
                                            <li className="list-group-item d-flex justify-content-between">
                                                <span>Call Preference</span>
                                                <strong>{getCallPreferenceText(reminderSettings.callPreference)}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between">
                                                <span>Appointment Summary</span>
                                                <strong>{getPreferenceText(reminderSettings.apptSummary)}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between">
                                                <span>Reminder Time</span>
                                                <strong>{reminderSettings.reminderTime || 'Not set'}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between">
                                                <span>Same Day Reminder Time</span>
                                                <strong>{reminderSettings.sameDayReminderTime || 'Not set'}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between">
                                                <span>Days to Send Prebook Reminder</span>
                                                <strong>{reminderSettings.daysPrebookReminder || 'Not set'}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between">
                                                <span>Days to Send First Reminder</span>
                                                <strong>{reminderSettings.daysFirstReminder || 'Not set'}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between">
                                                <span>Days to Send Second Reminder</span>
                                                <strong>{reminderSettings.daysSecondReminder || 'Not set'}</strong>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between">
                                                <span>Minutes Earlier</span>
                                                <strong>{reminderSettings.minutesToAppt || '0'} mins</strong>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {isError && (
                    <div className="alert alert-danger mt-3">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {errorMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReminderTab;